// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- packages that compile this from source (forms) have no vite/client, and a wildcard module cannot be imported
/// <reference path="../assets.d.ts" />
import type * as React from 'react'

import { cn } from '../lib/cn'
import phonePhoto from './entry-photo-phone.webp'
import widePhoto from './entry-photo-wide.webp'

// What stands behind the entrance (packages/ui/docs/cornice-d-ingresso.md): the boy in the light,
// slowly coming closer, three lights over him, and the veil that makes the words above readable.

/** Whether the picture moves: not at all (reduced motion), held (paused or tab hidden), or on. */
export type EntryMotion = 'still' | 'paused' | 'running'

/** The animation, if the picture moves at all, frozen where it stands while paused. */
function ambient(motion: EntryMotion, animation: string) {
  if (motion === 'still') return undefined
  return cn(animation, motion === 'paused' && '[animation-play-state:paused]')
}

function Photo({ priority }: { priority: boolean }) {
  return (
    <picture className="block size-full">
      <source media="(min-width: 960px)" srcSet={widePhoto} />
      <img
        src={phonePhoto}
        alt=""
        width={1080}
        height={1440}
        decoding="async"
        fetchPriority={priority ? 'high' : 'low'}
        className="size-full object-cover object-[51%_50%] entry:object-[50%_54%]"
      />
    </picture>
  )
}

const LAYER = 'absolute inset-0 origin-[51%_60%] will-change-transform entry:origin-[50%_62%]'

/* The growth: two copies of the photo take turns coming closer, the new one dissolving in over
   the old one as it ends. The second only starts once the first is half way. */
function GrowingPhoto({ motion }: { motion: EntryMotion }) {
  return (
    <>
      <div className={cn(LAYER, ambient(motion, 'animate-entry-grow-a'))}>
        <Photo priority />
      </div>
      {motion === 'still' ? null : (
        <div className={cn(LAYER, 'opacity-0', ambient(motion, 'animate-entry-grow-b'))}>
          <Photo priority={false} />
        </div>
      )}
    </>
  )
}

const RAY = 'absolute inset-y-0 blur-sm entry-ray'

/** The lights: a mist on the ground, three blades of light along the beam, a flickering glow. */
function LightEffects({ motion }: { motion: EntryMotion }) {
  return (
    <>
      <div
        className={cn(
          'absolute -right-1/4 -bottom-[5%] -left-1/4 top-[38%] mix-blend-screen entry-fog',
          ambient(motion, 'animate-entry-fog'),
        )}
      />
      <div className="absolute -inset-1/4 -rotate-24 mix-blend-screen entry-rays">
        <i className={cn(RAY, 'left-[40%] w-[11%]', ambient(motion, 'animate-entry-ray'))} />
        <i className={cn(RAY, 'left-[47%] w-[6%]', ambient(motion, 'animate-entry-ray-narrow'))} />
        <i className={cn(RAY, 'left-[33%] w-[8%]', ambient(motion, 'animate-entry-ray-wide'))} />
      </div>
      <div
        className={cn(
          'absolute inset-0 mix-blend-screen entry-beam',
          ambient(motion, 'animate-entry-flicker'),
        )}
      />
    </>
  )
}

export interface EntryBackdropProps {
  motion: EntryMotion
  /** The picture rises from the black once, as the frame opens. */
  opening: boolean
  /** What replaces the growing photo; the lights and the veil stay over it. */
  background?: React.ReactNode
}

export function EntryBackdrop({ motion, opening, background }: EntryBackdropProps) {
  return (
    <div aria-hidden="true" className="absolute inset-0 isolate overflow-hidden">
      <div className={cn('absolute inset-0', opening && 'animate-entry-stage')}>
        {background ?? <GrowingPhoto motion={motion} />}
        <LightEffects motion={motion} />
      </div>
      <div className="absolute inset-0 entry-veil" />
    </div>
  )
}
